// Registro dos ícones fotorrealistas gerados por scripts/render-vehicle-icons/
// a partir de .glb (ver src/assets/vehicle-icons/generated/<modelKey>/<hex>.png).
// import.meta.glob pega tudo automaticamente — adicionar um modelo novo não
// exige tocar neste arquivo, só gerar os PNGs na pasta certa.
const modules = import.meta.glob("../../assets/vehicle-icons/generated/*/*.png", {
  eager: true,
  import: "default",
}) as Record<string, string>;

const FILE_RE = /\/generated\/([^/]+)\/([0-9a-f]{6})\.png$/i;

function buildRegistry(): Record<string, Record<string, string>> {
  const registry: Record<string, Record<string, string>> = {};
  for (const [filePath, url] of Object.entries(modules)) {
    const match = filePath.match(FILE_RE);
    if (!match) continue;
    const [, modelKey, hex] = match;
    (registry[modelKey] ??= {})[hex.toLowerCase()] = url;
  }
  return registry;
}

// { [modelKey]: { [hexSemHash]: url } } — ex.: PHOTO_REGISTRY.hatchback["22c55e"]
export const PHOTO_REGISTRY: Record<string, Record<string, string>> = buildRegistry();

export function resolvePhotoUrl(modelKey: string, hexColor: string): string | undefined {
  return PHOTO_REGISTRY[modelKey]?.[hexColor.replace("#", "").toLowerCase()];
}

// Variante "Realista" (2026-10-27): mesmos modelos/cores em
// generated-real/<modelKey>/<hex>.png (VEHICLE_ICONS_VARIANT=real no gerador).
const realModules = import.meta.glob("../../assets/vehicle-icons/generated-real/*/*.png", {
  eager: true,
  import: "default",
}) as Record<string, string>;
const REAL_FILE_RE = /\/generated-real\/([^/]+)\/([0-9a-f]{6})\.png$/i;
const REAL_REGISTRY: Record<string, Record<string, string>> = {};
for (const [filePath, url] of Object.entries(realModules)) {
  const match = filePath.match(REAL_FILE_RE);
  if (match) (REAL_REGISTRY[match[1]] ??= {})[match[2].toLowerCase()] = url;
}

export function resolveRealPhotoUrl(modelKey: string, hexColor: string): string | undefined {
  return REAL_REGISTRY[modelKey]?.[hexColor.replace("#", "").toLowerCase()];
}

// Ícone em PERSPECTIVA (2026-10-27): uma grade de quadros por cor, quadro i =
// rumo i*step° (0 = norte, sentido horário) — gerado por
// scripts/render-vehicle-icons/iso.ts em generated-iso/<modelKey>/<hex>.webp.
const isoSheets = import.meta.glob("../../assets/vehicle-icons/generated-iso/*/*.webp", {
  eager: true,
  import: "default",
}) as Record<string, string>;
const isoMetas = import.meta.glob("../../assets/vehicle-icons/generated-iso/*/meta.json", {
  eager: true,
  import: "default",
}) as Record<string, IsoSheetMeta>;

export interface IsoSheetMeta {
  step: number;
  frames: number;
  cols: number;
  rows: number;
  frame: number;
}

const ISO_REGISTRY: Record<string, { meta: IsoSheetMeta; sheets: Record<string, string> }> = {};
for (const [filePath, meta] of Object.entries(isoMetas)) {
  const key = filePath.match(/\/generated-iso\/([^/]+)\/meta\.json$/)?.[1];
  if (key) ISO_REGISTRY[key] = { meta, sheets: {} };
}
for (const [filePath, url] of Object.entries(isoSheets)) {
  const m = filePath.match(/\/generated-iso\/([^/]+)\/([0-9a-f]{6})\.webp$/i);
  if (m && ISO_REGISTRY[m[1]]) ISO_REGISTRY[m[1]].sheets[m[2].toLowerCase()] = url;
}

export function resolveIsoSheet(modelKey: string, hexColor: string): { url: string; meta: IsoSheetMeta } | undefined {
  const entry = ISO_REGISTRY[modelKey];
  const url = entry?.sheets[hexColor.replace("#", "").toLowerCase()];
  return entry && url ? { url, meta: entry.meta } : undefined;
}

// iconMapping do deck.gl pra uma grade (f0…fN, âncora no centro do quadro).
const mappingCache = new Map<string, Record<string, { x: number; y: number; width: number; height: number; anchorY: number }>>();
export function isoIconMapping(meta: IsoSheetMeta) {
  const k = `${meta.frames}/${meta.cols}/${meta.frame}`;
  let m = mappingCache.get(k);
  if (!m) {
    m = {};
    for (let i = 0; i < meta.frames; i++) {
      m[`f${i}`] = { x: (i % meta.cols) * meta.frame, y: Math.floor(i / meta.cols) * meta.frame, width: meta.frame, height: meta.frame, anchorY: meta.frame / 2 };
    }
    mappingCache.set(k, m);
  }
  return m;
}

export function isoFrameFor(meta: IsoSheetMeta, bearingDeg: number): string {
  const a = ((bearingDeg % 360) + 360) % 360;
  return `f${Math.round(a / meta.step) % meta.frames}`;
}
